import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../helpers/api";
import type {
  ListResponse,
  ProvisioningUser,
} from "../../../onboarding-tracking/types";

/**
 * Google Ads customer ids appear in two spellings across the two systems
 * (`123-456-7890` on the platform user, bare digits on the tracking store).
 * Digits-only is the join key; empty/null normalizes to null so a missing id
 * never joins to anything.
 */
export function normalizeCustomerId(id: string | null | undefined): string | null {
  const digits = (id ?? "").replace(/\D/g, "");
  return digits || null;
}

// The backend list endpoint clamps `limit` at 100 (@Max(100) on the DTO), so
// the full pipeline census pages through. It is one in-memory join per page
// on the server; the whole set is ~100-200 rows.
const PAGE_LIMIT = 100;

async function fetchAllPipelines(): Promise<ProvisioningUser[]> {
  const first: ListResponse = await api.semProvisioning.list({
    page: 1,
    limit: PAGE_LIMIT,
  });
  const rows = [...first.data];
  const pages = Math.max(1, Math.ceil(first.total / PAGE_LIMIT));
  for (let page = 2; page <= pages; page++) {
    const next: ListResponse = await api.semProvisioning.list({
      page,
      limit: PAGE_LIMIT,
    });
    rows.push(...next.data);
  }
  return rows;
}

/**
 * Every SEM-provisioning pipeline row, un-paginated — the onboarding half of
 * the merged fleet table, and the source the pipeline drawer reads.
 *
 * The query key shares the old onboarding page's `"sem-provisioning"` prefix
 * on purpose: the drawer's step mutations (mark complete / retry / confirm
 * tracking) invalidate that prefix, so this census refetches with them.
 */
export function useOnboardingPipelines(enabled = true) {
  return useQuery<ProvisioningUser[]>({
    queryKey: ["sem-provisioning", "all"],
    queryFn: fetchAllPipelines,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Auto-refresh only while some pipeline is actively provisioning —
    // same cheap guard the standalone onboarding page used.
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!rows) return false;
      const hasActive = rows.some(
        (u) =>
          u.overallStatus === "IN_PROGRESS" ||
          u.overallStatus === "NOT_STARTED",
      );
      return hasActive ? 10_000 : false;
    },
  });
}
